// src/components/Company/CompanyLandingRedirect.jsx
//
// Where a company user lands when they sign in.
//
// /company (bare) is the sign-in destination: Login.js and OAuthCallback.jsx
// both send a GROUP there and nothing else routes to it, so this is the one
// place that can choose a landing without hijacking a deliberate click. The
// nav's own Dashboard link points at /company/dashboard and is untouched.
//
// THE DECK OUTRANKS THE DASHBOARD. Today's curated matches are the only thing
// on the account with a clock on it: the deck freezes on a UTC calendar day, a
// card left unswiped is simply gone at the next refresh, and the dashboard
// cannot show the provider behind a number. So a group with cards still to
// work lands on the cards. A group with none lands on the dashboard, which is
// the summary they came for.
//
// `matches` from /company/daily-matches is ALREADY the unswiped set — the
// service filters swiped providers out server-side before it answers — so its
// length is the question, and there is no second call to reconcile.
//
// This is a landing decision, never a gate: any answer we cannot get in time
// resolves to the dashboard. A failed read, a slow read, and an empty deck all
// land in the same place, so the sign-in path can never be held open by this.
import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import companyDailyDeckService from '../../services/companyDailyDeckService';
import LoadingPanel from '../Group/GroupProviderSwipe/components/LoadingPanel';

const DASHBOARD = '/company/dashboard';
const PROVIDERS = '/company/providers';

// A landing is not worth a spinner that outlives the page it is deciding
// between. Past this, the dashboard wins and the deck stays one nav click away.
export const DECK_LOOKUP_TIMEOUT_MS = 2500;

const CompanyLandingRedirect = () => {
  const location = useLocation();
  // Callers that arrive with a query string are asking for something specific
  // (?view=settings from the payment return, ?view=groupMatches from the OAuth
  // anonymous-swipe path). Those keep the destination they have always had —
  // this only decides the bare landing.
  const isBareLanding = !location.search;

  const [destination, setDestination] = useState(isBareLanding ? null : DASHBOARD);
  const settledRef = useRef(false);

  useEffect(() => {
    if (!isBareLanding) return undefined;

    let alive = true;
    const settle = (to) => {
      if (!alive || settledRef.current) return;
      settledRef.current = true;
      setDestination(to);
    };

    const timer = setTimeout(() => settle(DASHBOARD), DECK_LOOKUP_TIMEOUT_MS);

    // A peek: counting cards at sign-in is not a person looking at the deck.
    // Premium activation is handled only at an eligible D5 signup.
    companyDailyDeckService
      .getDailyDeck({ peek: true })
      .then((deck) => {
        settle(Array.isArray(deck?.matches) && deck.matches.length > 0 ? PROVIDERS : DASHBOARD);
      })
      .catch(() => settle(DASHBOARD));

    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [isBareLanding]);

  if (!destination) {
    return <LoadingPanel title="Loading your account..." />;
  }

  return <Navigate to={destination} replace />;
};

export default CompanyLandingRedirect;
